import {
    Account,
    Server,
    Action,
    GetV2ServersResult,
    GetV2LoadBalancersResult,
    Rename,
    Image,
    SampleSet,
    SampleSetResponse,
    DataUsage,
    GetV2ImagesResult,
    Region,
    Vpc,
    Size,
    SshKey,
    Software,
    GetV2ServersServerIdBackupsResult,
    TakeBackup,
    ImageRequest,
    ImageDownload,
    ChangeBackupSchedule,
    ChangeOffsiteBackupLocation,
    ChangeManageOffsiteBackupCopies,
    UploadImageRequest,
    ResizeDisk,
    DeleteDisk,
    AddDisk,
    AvailableAdvancedServerFeatures,
    ChangeAdvancedFeatures,
    LicensedSoftware,
    Resize,
    DataInterval,
    PasswordReset,
    Rebuild,
    Kernel,
    PostV2FailoverIpsServerIdResult,
    PostV2ReverseNamesIpv6Result,
    ReverseNameserversRequest,
    CreateVpcRequest,
    VpcMember,
    GetV2VpcsVpcIdMembersResult,
    PatchVpcRequest,
    ThresholdAlert,
    ThresholdAlertRequest,
    SshKeyRequest,
    PutV2AccountKeysKeyIdResult,
    LoadBalancer,
    CreateLoadBalancerRequest,
    PutV2LoadBalancersLoadBalancerIdResult,
    ActionLink,
    AdvancedFirewallRule,
    Balance,
    Invoice,
    CloneUsingBackup,
    Domain,
    DomainRecord,
    GetV2DomainsDomainNameRecordsResult,
    DomainRecordRequest,
    PutV2DomainsDomainNameRecordsRecordIdResult,
    LoadBalancerAvailabilityOption,
    DomainRequest,
    Console,
    ServiceLevel,
    PrePaymentFrequencyOption,
    ChangePaymentFrequencyRequest,
    UpdateDomainRecordRequest,
    DomainRefreshRequest,
    AdvancedFirewallRuleRequest,
    GetV2ImagesRequest,
    GetV2SamplesetsServerIdLatestRequest,
    FailoverIpsRequest,
    ProblemDetails,
} from './ApiModel';

import * as OpenApi from './OpenApiClient';
import { PagedItem, PageSpec, pageSpecAsUrl, asPagedItems } from './helper/PagingHelper';
import { BackupImage, backupImages } from './helper/BackupHelper';
import { hasItem, hasValue } from './helper/ArrayHelper';
import WebApiClient from './WebApiClient';

export interface IApiClient {
    getUnpaidInvoices(): Promise<Invoice[]>;
    getAlertedServerIds(): Promise<number[]>;
    getAccount(): Promise<Account>;

    setPartnerServer(serverId: number, partnerId?: number | null): Promise<void>;
    actionProceed(actionId: number, proceed: boolean): Promise<void>;
    rebuild(serverId: number, rebuild: Rebuild): Promise<Action | undefined>;
    passwordReset(serverId: number, reset: PasswordReset): Promise<Action | undefined>;
    resizeDisk(serverId: number, resize: ResizeDisk): Promise<Action | undefined>;
    deleteDisk(serverId: number, deleteRequest: DeleteDisk): Promise<Action | undefined>;
    addDisk(serverId: number, add: AddDisk): Promise<Action | undefined>;

    setManageOffsiteBackupCopies(serverId: number, manageUpdate: ChangeManageOffsiteBackupCopies): Promise<void>;
    setOffsiteBackupLocation(serverId: number, locationUpdate: ChangeOffsiteBackupLocation): Promise<void>;
    updateBackupSchedule(serverId: number, update: ChangeBackupSchedule): Promise<void>;
    getDownloadLinksForImage(imageId: number, cancellationToken?: AbortSignal): Promise<ImageDownload>;
    updateImage(imageId: number, update: ImageRequest): Promise<Image>;
    takeBackup(serverId: number, takeBackup: TakeBackup): Promise<Action | undefined>;
    attachBackup(serverId: number, backupId: number): Promise<Action | undefined>;
    detachBackup(serverId: number): Promise<Action | undefined>;
    restoreBackup(serverId: number, backupIdOrSlug: number | string): Promise<Action | undefined>;
    unCancelServer(serverId: number): Promise<void>;
    cancelServer(serverId: number, reason: string): Promise<void>;
    renameServer(serverId: number, name: Rename): Promise<Action | undefined>;
    changeServerPaymentFrequency(serverId: number, change: ChangePaymentFrequencyRequest): Promise<Server>;
    /* Servers */
    getServers(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Server>>;
    getServer(id: number, cancellationToken?: AbortSignal): Promise<Server>;
    getServerByHostname(serverHostname: string): Promise<Server>;
    getServerActions(id: number, pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Action>>;
    getServerBackups(
        serverId: number,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<BackupImage>>;
    getServerAvailableFailoverIps(serverId: number, pageSpec?: PageSpec): Promise<PagedItem<string>>;
    getServerUserData(serverId: number, cancellationToken?: AbortSignal): Promise<string | null>;
    getConsoleForServer(serverId: number, cancellationToken?: AbortSignal): Promise<Console>;
    rebootServer(id: number): Promise<Action | undefined>;
    powerCycleServer(id: number): Promise<Action | undefined>;
    getLatestSampleSet(
        serverId: number,
        interval?: DataInterval,
        cancellationToken?: AbortSignal
    ): Promise<SampleSet | null>;
    getCurrentDataUsage(serverId: number, cancellationToken?: AbortSignal): Promise<DataUsage>;
    pingServer(serverId: number): Promise<Action | undefined>;
    uptimeServer(serverId: number): Promise<Action | undefined>;
    disableIpv6Server(serverId: number): Promise<Action | undefined>;
    enableIpv6Server(serverId: number): Promise<Action | undefined>;
    enableBackupsServer(serverId: number): Promise<Action | undefined>;
    updateReverseName(serverId: number, ipv4_address: string, reverse_name: string): Promise<Action | undefined>;
    getReverseNameServers(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<string>>;
    updateReverseNameServers(serverId: number, name_servers: string[]): Promise<Action | undefined>;
    updateCustomerReverseNameServers(name_servers: string[]): Promise<void>;
    getOperatingSystems(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Image>>;
    getRegions(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Region>>;
    getVPCs(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Vpc>>;
    getSizes(
        serverId?: number,
        osSlug?: string,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<Size>>;
    getServiceLevels(cancellationToken?: AbortSignal): Promise<ServiceLevel[]>;
    getPaymentFrequencies(cancellationToken?: AbortSignal): Promise<PrePaymentFrequencyOption[]>;
    getSshKeys(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<SshKey>>;
    getSoftware(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Software>>;
    createVpc(createVpc: CreateVpcRequest): Promise<Vpc>;
    getVPC(vcpId: number): Promise<Vpc>;
    getVPCMembers(vpcId: number, pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<VpcMember>>;
    setFailoverIps(serverId: number, selected: FailoverIpsRequest): Promise<Action | undefined>;
    getServerFirewallRules(serverId: number, cancellationToken?: AbortSignal): Promise<AdvancedFirewallRule[]>;
    getDomains(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Domain>>;
    getDomainRecords(
        domainName: string,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<DomainRecord>>;
    getNameServers(): Promise<string[]>;

    uploadImage(serverId: number, upload: UploadImageRequest): Promise<Action | undefined>;
    getAvailableAdvancedFeatures(
        serverId: number,
        cancellationToken?: AbortSignal
    ): Promise<AvailableAdvancedServerFeatures>;
    updateAdvancedFeatures(serverId: number, request: ChangeAdvancedFeatures): Promise<Action | undefined>;
    getServerLicenses(
        serverId: number,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<LicensedSoftware>>;
    resize(serverId: number, resize: Resize): Promise<Action | undefined>;
    getSampleSets(
        serverId: number,
        durationType: DataInterval,
        startTime: string,
        endTime: string,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<SampleSet>>;
    getKernelsForServer(serverId: number, pageSpec?: PageSpec): Promise<PagedItem<Kernel>>;
    changeKernel(serverId: number, kernelId: number): Promise<Action | undefined>;
    changeNetwork(serverId: number, targetVpcId: number | null): Promise<Action | undefined>;
    changePortBlocking(serverId: number, enabled: boolean): Promise<Action | undefined>;
    changeSourceDestCheck(serverId: number, enabled: boolean): Promise<Action | undefined>;
    changeSeparatePrivateNetworkInterface(serverId: number, enabled: boolean): Promise<Action | undefined>;
    changeVpcIpv4(serverId: number, currentAddress: string, newAddress: string): Promise<Action | undefined>;
    getAlertThresholds(serverId: number, cancellationToken?: AbortSignal): Promise<ThresholdAlert[]>;
    changeAlertThresholds(serverId: number, thresholds: ThresholdAlertRequest[]): Promise<void>;
    changeAdvancedFirewallRules(serverId: number, req: AdvancedFirewallRuleRequest[]): Promise<Action | undefined>;
    cloneUsingBackup(serverId: number, req: CloneUsingBackup): Promise<Action | undefined>;

    /* Networks */
    patchNetwork(vpcId: number, update: PatchVpcRequest): Promise<Vpc>;
    deleteNetwork(vpcId: number): Promise<void>;

    createSshKey(keyInfo: SshKeyRequest): Promise<SshKey>;
    updateSshKey(keyId: number, keyInfo: SshKeyRequest): Promise<SshKey>;
    deleteSshKey(keyId: number): Promise<void>;

    /* Load Balancer */
    getLoadBalancers(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<LoadBalancer>>;
    getLoadBalancer(loadBalancerId: number, cancellationToken?: AbortSignal): Promise<LoadBalancer>;
    getLoadBalancerAvailability(): Promise<LoadBalancerAvailabilityOption[]>;

    createLoadBalancer(values: CreateLoadBalancerRequest): Promise<LoadBalancer>;
    deleteLoadBalancer(loadBalancerId: number): Promise<void>;
    updateLoadBalancer(
        loadBalancerId: number,
        loadBalancer: LoadBalancer
    ): Promise<{ loadBalancer: LoadBalancer; action?: ActionLink }>;

    /* Customer balance/invoice */
    getBalance(cancellationToken?: AbortSignal): Promise<Balance>;
    getInvoice(invoiceId: number, cancellationToken?: AbortSignal): Promise<Invoice>;
    getInvoices(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Invoice>>;

    /* Domain Records */
    addDomainHosting(req: DomainRequest): Promise<Domain>;
    removeDomainHosting(domainName: string): Promise<void>;
    getHostedDomain(domainName: string): Promise<Domain>;

    createDomainRecord(domainName: string, values: DomainRecordRequest): Promise<DomainRecord>;
    updateDomainRecord(domainName: string, id: number, values: UpdateDomainRecordRequest): Promise<DomainRecord>;
    deleteDomainRecord(domainName: string, id: number): Promise<void>;

    /* Actions */
    getActions(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Action>>;
    getAction(id: number, cancellationToken?: AbortSignal): Promise<Action>;

    changeRegion(serverId: number, region: string): Promise<Action | undefined>;

    refreshNameserverCache(request: DomainRefreshRequest): Promise<void>;
}

function asQueryParams<T extends Record<string, string | boolean | number | undefined>>(params: T): string {
    const querySegments: string[] = [];
    for (const key of Object.keys(params)) {
        const val = params[key];
        if (hasValue(val)) {
            querySegments.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
        }
    }
    return querySegments.join('&');
}

/** Bind the first `client` parameter of an OpenApiClient function to this instance. */
function delegate<A extends unknown[], R>(
    target: WebApiClient,
    fn: (client: WebApiClient, ...args: A) => R
): (...args: A) => R {
    return (...args: A) => fn(target, ...args);
}

export default class ApiClient extends WebApiClient implements IApiClient {
    getActions = delegate(this, OpenApi.getActions);
    getAction = delegate(this, OpenApi.getAction);
    getServer = delegate(this, OpenApi.getServer);
    getServerActions = delegate(this, OpenApi.getServerActions);
    getAlertedServerIds = delegate(this, OpenApi.getServersThresholdAlerts);
    getServerUserData = delegate(this, OpenApi.getServerUserData);
    getConsoleForServer = delegate(this, OpenApi.getServerConsole);
    getRegions = delegate(this, OpenApi.getRegions);
    getCurrentDataUsage = delegate(this, OpenApi.getDataUsageCurrent);
    getReverseNameServers = delegate(this, OpenApi.getReverseNamesIpv6);
    getVPCs = delegate(this, OpenApi.getVpcs);
    getSshKeys = delegate(this, OpenApi.getAccountKeys);
    getSoftware = delegate(this, OpenApi.getSoftwares);
    getServiceLevels = delegate(this, OpenApi.getSizesServiceLevels);
    getPaymentFrequencies = delegate(this, OpenApi.getSizesPaymentFrequencies);
    changeServerPaymentFrequency = delegate(this, OpenApi.postServerPaymentFrequency);
    performServerAction = delegate(this, OpenApi.postServerActions);
    createVpc = delegate(this, OpenApi.postVpcs);
    updateImage = delegate(this, OpenApi.putImage);
    getDownloadLinksForImage = delegate(this, OpenApi.getImageDownload);
    uploadImage = delegate(this, OpenApi.postServerBackups);
    getAvailableAdvancedFeatures = delegate(this, OpenApi.getServerAvailableAdvancedFeatures);
    getServerLicenses = delegate(this, OpenApi.getServerSoftwares);
    getKernelsForServer = delegate(this, OpenApi.getServerKernels);
    getServerAvailableFailoverIps = delegate(this, OpenApi.getFailoverIpAvailable);
    getVPC = delegate(this, OpenApi.getVpc);
    getDomains = delegate(this, OpenApi.getDomains);
    patchNetwork = delegate(this, OpenApi.patchVpc);
    deleteNetwork = delegate(this, OpenApi.deleteVpc);
    getBalance = delegate(this, OpenApi.getCustomersMyBalance);
    getInvoices = delegate(this, OpenApi.getCustomersMyInvoices);
    getInvoice = delegate(this, OpenApi.getCustomersMyInvoice);
    getUnpaidInvoices = delegate(this, OpenApi.getCustomersMyUnpaidPaymentFailedInvoices);
    getAccount = delegate(this, OpenApi.getAccount);
    createSshKey = delegate(this, OpenApi.postAccountKeys);
    getLoadBalancer = delegate(this, OpenApi.getLoadBalancer);
    createLoadBalancer = delegate(this, OpenApi.postLoadBalancers);
    deleteLoadBalancer = delegate(this, OpenApi.deleteLoadBalancer);
    getLoadBalancerAvailability = delegate(this, OpenApi.getLoadBalancersAvailability);
    getServerFirewallRules = delegate(this, OpenApi.getServerAdvancedFirewallRules);
    getAlertThresholds = delegate(this, OpenApi.getServerThresholdAlerts);
    getSizes = delegate(this, OpenApi.getSizes);
    getSampleSets = delegate(this, OpenApi.getSampleset);
    cancelServer = delegate(this, OpenApi.deleteServer);
    getNameServers = delegate(this, OpenApi.getDomainsNameservers);
    addDomainHosting = delegate(this, OpenApi.postDomains);
    getHostedDomain = delegate(this, OpenApi.getDomain);
    removeDomainHosting = delegate(this, OpenApi.deleteDomain);
    createDomainRecord = delegate(this, OpenApi.postDomainRecords);
    deleteDomainRecord = delegate(this, OpenApi.deleteDomainRecord);
    refreshNameserverCache = delegate(this, OpenApi.postDomainsRefreshNameserverCache);

    async getServers(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Server>> {
        const pageLink = pageSpecAsUrl(pageSpec, '/servers');
        const result = await this.JsonGet<GetV2ServersResult>(pageLink, cancellationToken);
        return asPagedItems(result.servers, result);
    }

    // The generated getLoadBalancers takes a leading `name` filter param the panel does not
    // use; a hand-written override keeps the IApiClient.getLoadBalancers(pageSpec, ...) signature.
    async getLoadBalancers(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<LoadBalancer>> {
        const pageLink = pageSpecAsUrl(pageSpec, '/load_balancers');
        const result = await this.JsonGet<GetV2LoadBalancersResult>(pageLink, cancellationToken);
        return asPagedItems(result.load_balancers, result);
    }

    async getServerByHostname(serverHostname: string, cancellationToken?: AbortSignal): Promise<Server> {
        const pageLink = pageSpecAsUrl(
            { page: 1, perPage: 200 }, // while we should get 1 or 0 results, this provides backwards compatibility with the old API
            `/servers?hostname=${encodeURIComponent(serverHostname)}`
        );
        const result = await this.JsonGet<GetV2ServersResult>(pageLink, cancellationToken);
        const servers = result.servers;
        if (hasItem(servers)) {
            return servers[0];
        } else {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw {
                type: 'NotFound',
                title: 'Server Not Found',
                status: 404,
                detail: 'The server could not be found.',
            } as ProblemDetails;
        }
    }

    async getServerBackups(
        serverId: number,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<BackupImage>> {
        const pageLink = pageSpecAsUrl(pageSpec, `/servers/${serverId}/backups`);
        const result = await this.JsonGet<GetV2ServersServerIdBackupsResult>(pageLink, cancellationToken);
        return asPagedItems(backupImages(result.backups), result);
    }

    async getOperatingSystems(pageSpec?: PageSpec, cancellationToken?: AbortSignal): Promise<PagedItem<Image>> {
        const pageLink = pageSpecAsUrl(
            pageSpec,
            `/images?${asQueryParams<Partial<GetV2ImagesRequest>>({ type: 'distribution' })}`
        );
        const result = await this.JsonGet<GetV2ImagesResult>(pageLink, cancellationToken);
        return asPagedItems(result.images, result);
    }

    rebootServer(id: number): Promise<Action | undefined> {
        return this.performServerAction(id, { type: 'reboot' });
    }

    renameServer(id: number, name: Rename): Promise<Action | undefined> {
        return this.performServerAction(id, name);
    }

    powerCycleServer(id: number): Promise<Action | undefined> {
        return this.performServerAction(id, { type: 'power_cycle' });
    }

    async getLatestSampleSet(
        serverId: number,
        interval?: DataInterval,
        cancellationToken?: AbortSignal
    ): Promise<SampleSet | null> {
        const selectedInterval = interval ?? 'five-minute';

        return (
            (
                await this.JsonGet<SampleSetResponse>(
                    `/samplesets/${serverId}/latest?${asQueryParams<Partial<GetV2SamplesetsServerIdLatestRequest>>({
                        data_interval: selectedInterval,
                    })}`,
                    cancellationToken
                )
            ).sample_set ?? null
        );
    }

    pingServer(id: number): Promise<Action | undefined> {
        return this.performServerAction(id, { type: 'ping' });
    }

    uptimeServer(id: number): Promise<Action | undefined> {
        return this.performServerAction(id, { type: 'uptime' });
    }

    disableIpv6Server(id: number): Promise<Action | undefined> {
        return this.performServerAction(id, { type: 'change_ipv6', enabled: false });
    }

    enableIpv6Server(id: number): Promise<Action | undefined> {
        return this.performServerAction(id, { type: 'enable_ipv6' });
    }

    enableBackupsServer(id: number): Promise<Action | undefined> {
        return this.performServerAction(id, { type: 'enable_backups' });
    }

    updateReverseName(id: number, ipv4_address: string, reverse_name: string): Promise<Action | undefined> {
        return this.performServerAction(id, {
            type: 'change_reverse_name',
            ipv4_address: ipv4_address,
            reverse_name: reverse_name,
        });
    }

    updateReverseNameServers(serverId: number, name_servers: string[]): Promise<Action | undefined> {
        return this.performServerAction(serverId, {
            type: 'change_ipv6_reverse_nameservers',
            ipv6_reverse_nameservers: name_servers,
        });
    }

    async updateCustomerReverseNameServers(name_servers: string[]): Promise<void> {
        // Use JsonRequest (not the generated JsonPost binding) so a no-op save that returns 204 No
        // Content is treated as success instead of throwing on the empty response body. The endpoint
        // returns an UpdateDnsCommand action, but the account-wide update is deliberately untracked
        // (there is no customer-level ActionStore/progress UI for it), so the action is discarded.
        await this.JsonRequest<PostV2ReverseNamesIpv6Result>('/reverse_names/ipv6', 'POST', {
            reverse_nameservers: name_servers,
        } satisfies ReverseNameserversRequest);
    }

    async unCancelServer(serverId: number): Promise<void> {
        await this.performServerAction(serverId, { type: 'uncancel' });
    }

    restoreBackup(serverId: number, backupIdOrSlug: string | number): Promise<Action | undefined> {
        return this.performServerAction(serverId, { type: 'restore', image: backupIdOrSlug });
    }

    attachBackup(serverId: number, backupId: number): Promise<Action | undefined> {
        return this.performServerAction(serverId, { type: 'attach_backup', image: backupId });
    }

    takeBackup(serverId: number, takeBackup: TakeBackup): Promise<Action | undefined> {
        return this.performServerAction(serverId, takeBackup);
    }

    detachBackup(serverId: number): Promise<Action | undefined> {
        return this.performServerAction(serverId, { type: 'detach_backup' });
    }

    async updateBackupSchedule(serverId: number, update: ChangeBackupSchedule): Promise<void> {
        await this.performServerAction(serverId, update);
    }

    async setManageOffsiteBackupCopies(serverId: number, manageUpdate: ChangeManageOffsiteBackupCopies): Promise<void> {
        await this.performServerAction(serverId, manageUpdate);
    }

    async setOffsiteBackupLocation(serverId: number, locationUpdate: ChangeOffsiteBackupLocation): Promise<void> {
        await this.performServerAction(serverId, locationUpdate);
    }

    resizeDisk(serverId: number, resize: ResizeDisk): Promise<Action | undefined> {
        return this.performServerAction(serverId, resize);
    }

    deleteDisk(serverId: number, deleteRequest: DeleteDisk): Promise<Action | undefined> {
        return this.performServerAction(serverId, deleteRequest);
    }

    addDisk(serverId: number, add: AddDisk): Promise<Action | undefined> {
        return this.performServerAction(serverId, add);
    }

    updateAdvancedFeatures(serverId: number, request: ChangeAdvancedFeatures): Promise<Action | undefined> {
        return this.performServerAction(serverId, request);
    }

    resize(serverId: number, resize: Resize): Promise<Action | undefined> {
        return this.performServerAction(serverId, resize);
    }

    passwordReset(serverId: number, reset: PasswordReset): Promise<Action | undefined> {
        return this.performServerAction(serverId, reset);
    }

    rebuild(serverId: number, rebuild: Rebuild): Promise<Action | undefined> {
        return this.performServerAction(serverId, rebuild);
    }

    changeKernel(serverId: number, kernelId: number): Promise<Action | undefined> {
        return this.performServerAction(serverId, {
            type: 'change_kernel',
            kernel: kernelId,
        });
    }

    async actionProceed(actionId: number, proceed: boolean): Promise<void> {
        await this.JsonRequest(`/actions/${actionId}/proceed`, 'POST', {
            proceed: proceed,
        });
    }

    async setFailoverIps(serverId: number, selected: FailoverIpsRequest): Promise<Action | undefined> {
        return (await this.JsonRequest<PostV2FailoverIpsServerIdResult>(`/failover_ips/${serverId}`, 'POST', selected))
            ?.action;
    }

    async setPartnerServer(serverId: number, partnerId: number): Promise<void> {
        await this.performServerAction(serverId, {
            type: 'change_partner',
            partner_server_id: partnerId,
        });
    }

    async getVPCMembers(
        vpcId: number,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<VpcMember>> {
        const pageLink = pageSpecAsUrl(pageSpec, `/vpcs/${vpcId}/members`);
        const res = await this.JsonGet<GetV2VpcsVpcIdMembersResult>(pageLink, cancellationToken);
        return asPagedItems(res.members, res);
    }

    changeNetwork(serverId: number, targetVpcId: number): Promise<Action | undefined> {
        return this.performServerAction(serverId, { type: 'change_network', vpc_id: targetVpcId });
    }

    changePortBlocking(serverId: number, enabled: boolean): Promise<Action | undefined> {
        return this.performServerAction(serverId, { type: 'change_port_blocking', enabled: enabled });
    }

    changeSourceDestCheck(serverId: number, enabled: boolean): Promise<Action | undefined> {
        return this.performServerAction(serverId, { type: 'change_source_and_destination_check', enabled: enabled });
    }

    changeSeparatePrivateNetworkInterface(serverId: number, enabled: boolean): Promise<Action | undefined> {
        return this.performServerAction(serverId, {
            type: 'change_separate_private_network_interface',
            enabled: enabled,
        });
    }

    changeVpcIpv4(serverId: number, currentAddress: string, newAddress: string): Promise<Action | undefined> {
        return this.performServerAction(serverId, {
            type: 'change_vpc_ipv4',
            current_ipv4_address: currentAddress,
            new_ipv4_address: newAddress,
        });
    }

    changeAdvancedFirewallRules(serverId: number, req: AdvancedFirewallRuleRequest[]): Promise<Action | undefined> {
        return this.performServerAction(serverId, {
            type: 'change_advanced_firewall_rules',
            firewall_rules: req,
        });
    }

    async changeAlertThresholds(serverId: number, thresholds: ThresholdAlertRequest[]): Promise<void> {
        await this.performServerAction(serverId, {
            type: 'change_threshold_alerts',
            threshold_alerts: thresholds,
        });
        return;
    }

    async updateSshKey(keyId: number, keyInfo: SshKeyRequest): Promise<SshKey> {
        return (await this.JsonPut<PutV2AccountKeysKeyIdResult>(`/account/keys/${keyId}`, keyInfo)).ssh_key;
    }

    async deleteSshKey(keyId: number): Promise<void> {
        await this.JsonDelete<unknown>(`/account/keys/${keyId}`);
    }

    async updateLoadBalancer(
        loadBalancerId: number,
        loadBalancer: LoadBalancer
    ): Promise<{ loadBalancer: LoadBalancer; action?: ActionLink }> {
        const res = await this.JsonPut<PutV2LoadBalancersLoadBalancerIdResult>(
            `/load_balancers/${loadBalancerId}`,
            loadBalancer
        );
        return {
            loadBalancer: res.load_balancer,
            action: res.links?.actions[0],
        };
    }

    cloneUsingBackup(serverId: number, req: CloneUsingBackup): Promise<Action | undefined> {
        return this.performServerAction(serverId, req);
    }

    async getDomainRecords(
        domainName: string,
        pageSpec?: PageSpec,
        cancellationToken?: AbortSignal
    ): Promise<PagedItem<DomainRecord>> {
        const pageLink = pageSpecAsUrl(pageSpec, `/domains/${encodeURIComponent(domainName)}/records`);
        const res = await this.JsonGet<GetV2DomainsDomainNameRecordsResult>(pageLink, cancellationToken);
        return asPagedItems(res.domain_records, res);
    }

    async updateDomainRecord(domainName: string, id: number, values: DomainRecordRequest): Promise<DomainRecord> {
        return (
            await this.JsonPut<PutV2DomainsDomainNameRecordsRecordIdResult>(
                `/domains/${encodeURIComponent(domainName)}/records/${id}`,
                values
            )
        ).domain_record;
    }

    async changeRegion(serverId: number, region: string): Promise<Action | undefined> {
        return this.performServerAction(serverId, { type: 'change_region', region: region });
    }
}
